import maplibregl from 'maplibre-gl';
import { RadarFrame } from '../services/LiveWeatherService';

export class CanvasRadarRenderer {
  private map: maplibregl.Map;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private imageCache: Map<string, HTMLImageElement> = new Map();
  private failedUrls: Set<string> = new Set();
  
  private currentFrame: RadarFrame | null = null;
  private currentOpacity: number = 0.65;
  private active: boolean = false;
  private lastDrawnImage: HTMLImageElement | null = null;

  private onMapMoveBound: () => void;

  // CONUS MRMS Grid extent: West -130.0, South 20.0, East -60.0, North 55.0
  private extent = {
    west: -130.0,
    south: 20.0,
    east: -60.0,
    north: 55.0
  };

  constructor(map: maplibregl.Map) {
    this.map = map;
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'live-radar-canvas';
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '2';

    const container = map.getContainer();
    container.appendChild(this.canvas);

    this.ctx = this.canvas.getContext('2d')!;
    this.onMapMoveBound = () => this.redraw();

    this.resizeCanvas();
    this.map.on('move', this.onMapMoveBound);
    this.map.on('moveend', this.onMapMoveBound);
    this.map.on('resize', () => {
      this.resizeCanvas();
      this.redraw();
    });
  }

  private resizeCanvas(): void {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = container.clientWidth * dpr;
    this.canvas.height = container.clientHeight * dpr;
    this.canvas.style.width = `${container.clientWidth}px`;
    this.canvas.style.height = `${container.clientHeight}px`;
    
    this.ctx.resetTransform();
    this.ctx.scale(dpr, dpr);
    this.ctx.imageSmoothingEnabled = false;
  }

  public update(frame: RadarFrame | null, opacity: number, active: boolean): void {
    this.currentFrame = frame;
    this.currentOpacity = opacity;
    this.active = active;
    this.redraw();
  }

  private loadImage(url: string): HTMLImageElement | null {
    if (this.imageCache.has(url)) {
      return this.imageCache.get(url)!;
    }
    if (this.failedUrls.has(url)) {
      return null;
    }

    const img = new Image();
    img.crossOrigin = 'use-credentials';
    img.onload = () => {
      this.imageCache.set(url, img);
      if (this.active && this.currentFrame && (this.currentFrame.tileUrl === url || this.currentFrame.tileUrl.includes(url) || url.includes(this.currentFrame.tileUrl))) {
        this.redraw();
      }
    };
    img.onerror = () => {
      this.failedUrls.add(url);
      // Graceful fallback: If a 4K image fails to load (e.g. 404 for an older scan), fall back to base 2K .webp
      if (url.includes('_4096.webp')) {
        const fallbackUrl = url.replace('_4096.webp', '.webp');
        if (!this.failedUrls.has(fallbackUrl) && !this.imageCache.has(fallbackUrl)) {
          const fallbackImg = new Image();
          fallbackImg.crossOrigin = 'use-credentials';
          fallbackImg.onload = () => {
            this.imageCache.set(fallbackUrl, fallbackImg);
            this.imageCache.set(url, fallbackImg);
            if (this.active && this.currentFrame && (this.currentFrame.tileUrl === url || this.currentFrame.tileUrl.includes(url) || url.includes(this.currentFrame.tileUrl))) {
              this.redraw();
            }
          };
          fallbackImg.onerror = () => {
            this.failedUrls.add(fallbackUrl);
          };
          fallbackImg.src = fallbackUrl;
        }
      }
    };
    img.src = url;
    return null;
  }

  public redraw(): void {
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, container.clientWidth * dpr, container.clientHeight * dpr);

    if (!this.active || !this.currentFrame || !this.currentFrame.tileUrl) {
      return;
    }

    const bounds = this.map.getBounds();
    if (!bounds) return;

    let imgUrl = this.currentFrame.tileUrl;
    let img: HTMLImageElement | null | undefined = this.imageCache.get(imgUrl);
    if (!img && imgUrl.includes('_4096.webp')) {
      const fallbackUrl = imgUrl.replace('_4096.webp', '.webp');
      img = this.imageCache.get(fallbackUrl);
    }
    if (!img) {
      img = this.loadImage(imgUrl);
    }

    if (img && img.complete && img.naturalWidth !== 0) {
      this.lastDrawnImage = img;
    } else if (this.lastDrawnImage && this.lastDrawnImage.complete && this.lastDrawnImage.naturalWidth !== 0) {
      img = this.lastDrawnImage;
    } else {
      return;
    }

    this.ctx.globalAlpha = this.currentOpacity;
    this.ctx.imageSmoothingEnabled = false;

    const SLICES = 128;
    const imgW = img.naturalWidth || img.width;
    const imgH = img.naturalHeight || img.height;
    const latSpan = this.extent.north - this.extent.south;

    for (let i = 0; i < SLICES; i++) {
      const frac1 = i / SLICES;
      const frac2 = (i + 1) / SLICES;

      const lat1 = this.extent.north - frac1 * latSpan;
      const lat2 = this.extent.north - frac2 * latSpan;

      const srcY1 = Math.floor(frac1 * imgH);
      const srcY2 = Math.ceil(frac2 * imgH);
      const srcH = Math.max(1, srcY2 - srcY1);

      const pTopLeft = this.map.project([this.extent.west, lat1]);
      const pBottomRight = this.map.project([this.extent.east, lat2]);

      const destX = pTopLeft.x;
      const destY = pTopLeft.y;
      const destW = pBottomRight.x - pTopLeft.x;
      const destH = Math.max(0.5, pBottomRight.y - pTopLeft.y);

      this.ctx.drawImage(img, 0, srcY1, imgW, srcH, destX, destY, destW, destH + 0.5);
    }

    this.ctx.globalAlpha = 1.0;
  }

  /**
   * Proactively pre-fetches viewport tile images for frames in the radar loop,
   * guaranteeing smooth animation playback and instant scrub response.
   */
  public preloadSequence(frames: RadarFrame[]): void {
    if (!frames || frames.length === 0) return;
    frames.forEach(frame => {
      if (frame.tileUrl) {
        this.loadImage(frame.tileUrl);
      }
    });
  }

  /**
   * Preloads and waits for the first N frames to load into imageCache before playback starts.
   */
  public async waitForFrames(frames: RadarFrame[], count: number = 8, timeoutMs: number = 1200): Promise<void> {
    if (!frames || frames.length === 0) return;
    this.preloadSequence(frames);

    const targetFrames = frames.slice(0, Math.min(frames.length, count));
    const promises = targetFrames.map(f => {
      if (!f.tileUrl) return Promise.resolve();
      const existing = this.imageCache.get(f.tileUrl);
      if (existing && existing.complete && existing.naturalWidth !== 0) {
        return Promise.resolve();
      }
      return new Promise<void>(resolve => {
        const img = new Image();
        img.crossOrigin = 'use-credentials';
        img.onload = () => {
          this.imageCache.set(f.tileUrl, img);
          resolve();
        };
        img.onerror = () => {
          if (f.tileUrl.includes('_4096.webp')) {
            const fallbackUrl = f.tileUrl.replace('_4096.webp', '.webp');
            const fallbackImg = new Image();
            fallbackImg.crossOrigin = 'use-credentials';
            fallbackImg.onload = () => {
              this.imageCache.set(fallbackUrl, fallbackImg);
              this.imageCache.set(f.tileUrl, fallbackImg);
              resolve();
            };
            fallbackImg.onerror = () => resolve();
            fallbackImg.src = fallbackUrl;
          } else {
            resolve();
          }
        };
        img.src = f.tileUrl;
      });
    });

    await Promise.race([
      Promise.all(promises),
      new Promise(resolve => setTimeout(resolve, timeoutMs))
    ]);
  }

  public clearCache(): void {
    this.imageCache.clear();
    this.failedUrls.clear();
    this.lastDrawnImage = null;
  }

  public clear(): void {
    this.active = false;
    this.lastDrawnImage = null;
    const container = this.map.getContainer();
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, container.clientWidth * dpr, container.clientHeight * dpr);
  }

  public destroy(): void {
    this.clear();
    this.map.off('move', this.onMapMoveBound);
    this.map.off('moveend', this.onMapMoveBound);
    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

export default CanvasRadarRenderer;
